import React, { useState } from 'react';
import { 
  GraduationCap, BookOpen, Sparkles, Download, Copy, Check, 
  Layers, RefreshCw, FileText, CheckCircle2, ArrowRight, 
  Satellite, Cpu, Landmark, ShieldCheck, ChevronRight, FileCheck,
  Upload, X, Trash2, AlertCircle, Paperclip
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';

interface PhdFullDissertationGeneratorProps {
  language: 'uk' | 'en' | 'pl';
  onLaunchSpecificAgent: (agentId: string) => void;
}

interface ChapterResult {
  id: string;
  title: string;
  stageName: string;
  content: string;
  isCompleted: boolean;
}

interface AttachedFile {
  name: string;
  size: number;
  text: string;
}

const STAGES = [
  { 
    id: 'intro', 
    agentId: 'phd-dissertation-lead-orchestrator', 
    nameUk: 'Вступ & Науковий Паспорт', 
    namePl: 'Wstęp & Paszport Naukowy',
    nameEn: 'Introduction & Scientific Passport',
    descUk: 'Актуальність, мета, 5 завдань, об\'єкт/предмет, 4 положення наукової новизни, апробація.',
    descPl: 'Aktualność, cel, 5 zadań badawczych, obiekt/przedmiot, 4 tezy nowości naukowej, aprobata.',
    descEn: 'Relevance, research aim, 5 objectives, object/subject, 4 novelty claims, peer review approval.'
  },
  { 
    id: 'ch1', 
    agentId: 'phd-global-trends-comparator', 
    nameUk: 'Розділ 1: Світові Тенденції & Огляд', 
    namePl: 'Rozdział 1: Trendy Światowe & Przegląd',
    nameEn: 'Chapter 1: Global Trends & Literature Review',
    descUk: 'Аналіз Copernicus, NASA ESO, Destination Earth, систем SSA/SST та 60+ Scopus джерел.',
    descPl: 'Analiza programów Copernicus, NASA ESO, Destination Earth, SSA/SST oraz 60+ źródeł Scopus.',
    descEn: 'Analysis of Copernicus, NASA ESO, Destination Earth, SSA/SST architectures, and 60+ Scopus citations.'
  },
  { 
    id: 'ch2', 
    agentId: 'phd-ukraine-capabilities-auditor', 
    nameUk: 'Розділ 2: Стан та Спроможності України', 
    namePl: 'Rozdział 2: Stan i Zdolności Ukrainy',
    nameEn: 'Chapter 2: State & Capabilities of Ukraine',
    descUk: 'Аудит інфраструктури ДКАУ, НЦУВКЗ, ГЦСК, законодавство, оцінка екоциду від війни.',
    descPl: 'Audyt infrastruktury SSAU, NCUVKZ, GCKS, ustawodawstwo, ocena szkód wojennych i ekobójstwa.',
    descEn: 'Audit of SSAU, NCUVKZ, GCKS infrastructure, regulatory frameworks, and warfare ecocide damage assessment.'
  },
  { 
    id: 'ch3', 
    agentId: 'phd-data-fusion-math-modeler', 
    nameUk: 'Розділ 3: Методологія & Data Fusion Моделі', 
    namePl: 'Rozdział 3: Metodologia & Modele Data Fusion',
    nameEn: 'Chapter 3: Methodology & Data Fusion Models',
    descUk: 'Математичний апарат комплексування ДЗЗ, СКАКО, ГНСС і спецконтролю (фільтри Кальмана).',
    descPl: 'Aparat matematyczny integracji teledetekcji, SSA, GNSS i kontroli specjalnej (filtry Kalmana UKF).',
    descEn: 'Mathematical framework for multi-source fusion of EO, SSA, GNSS, and special control (UKF Kalman filters).'
  },
  { 
    id: 'ch4', 
    agentId: 'phd-national-system-architect', 
    nameUk: 'Розділ 4: Архітектура Національної Системи', 
    namePl: 'Rozdział 4: Architektura Systemu Narodowego',
    nameEn: 'Chapter 4: National System Architecture',
    descUk: 'Проєкт НІС-ЕКО, 3-етапна дорожня карта 2026–2030, кошторис ТЕО та екологічний ефект.',
    descPl: 'Projekt NIS-EKO, 3-etapowa mapa drogowa 2026–2030, studium wykonalności i bilans korzyści.',
    descEn: 'NIS-ECO system blueprint, 3-phase 2026–2030 roadmap, cost-benefit feasibility, and ecological impact.'
  },
  { 
    id: 'conclusions', 
    agentId: 'phd-academic-compliance-bibliographer', 
    nameUk: 'Висновки, Література (ДСТУ/APA) & Додатки', 
    namePl: 'Wnioski, Bibliografia & Załączniki',
    nameEn: 'Conclusions, Bibliography (DSTU/APA) & Appendices',
    descUk: 'Синтез висновків, список 200+ джерел за ДСТУ 8302:2015 / APA 7th та додатки.',
    descPl: 'Synteza wniosków końcowych, wykaz 200+ źródeł wg norm bibliograficznych i załączniki A-C.',
    descEn: 'Synthesis of doctoral conclusions, 200+ references formatted to DSTU 8302:2015 / APA 7th, and appendices.'
  }
];

const DEMO_PRESETS = {
  uk: {
    authorName: 'Банах Андрій Степанович',
    scientificSupervisor: 'доктор технічних наук, старший науковий співробітник Присяжний Володимир Ілліч',
    institutionName: 'Державна наукова установа «Інститут екологічного відновлення та розвитку України»',
    regulatoryBasis: 'Наказ МОН України № 40 від 12.01.2017, Постанова КМУ № 44 від 12.01.2022 (зі змінами № 502 від 19.05.2023), ДСТУ 8302:2015, ДСТУ 4163:2020',
    topic: 'Науково-методологічні та технологічні засади створення Національної інтегрованої системи космічного моніторингу надзвичайних ситуацій та екологічної безпеки («НІС-ЕКО»)',
    specialty: '183 «Технології захисту довкілля» (інтеграція з 103 «Науки про Землю», 122 «Комп\'ютерні науки»)',
    objectSubject: 'Об\'єкт: Процеси екологічного та геофізичного моніторингу природно-техногенних систем; Предмет: Методи, математичні моделі та архітектура комплексування гетерогенних даних ДЗЗ, СКАКО, ГНСС і спецконтролю',
    targetSectors: [
      'Дистанційне зондування Землі (мультиспектральні Sentinel-2, радарні Sentinel-1 SAR, гіперспектральні)',
      'Система СКАКО (моніторинг космічного простору SSA/SST, захист супутників та ліній зв\'язку)',
      'Перманентні мережі ГНСС (моніторинг деформацій дамб ГЕС, карстів Солотвино та тропосфери ZTD)',
      'Спеціальний геофізичний контроль ГЦСК (сейсмоакустика, інфразвук, затоплення шахт Донбасу, ЗАЕС/ЧАЕС)',
      'Математичне комплексування Data Fusion (адаптивні фільтри Кальмана UKF, динамічні Байєсівські графи)',
      'Доказова база воєнного екоциду для міжнародних судових інстанцій та репарацій',
      'Національна інтегрована система НІС-ЕКО та взаємодія з програмою Copernicus ЄС'
    ]
  },
  pl: {
    authorName: 'Banach Andrij Stepanowycz',
    scientificSupervisor: 'dr hab. inż., prof. Wołodymyr I. Przysiażnyj',
    institutionName: 'Państwowa Instytucja Naukowa «Instytut Odbudowy i Rozwoju Ekologicznego Ukrainy»',
    regulatoryBasis: 'Zarządzenie Ministerstwa Edukacji i Nauki Ukrainy Nr 40, Rozporządzenie RMU Nr 44 (ze zm. Nr 502), DSTU 8302:2015, DSTU 4163:2020',
    topic: 'Naukowe, metodologiczne i technologiczne podstawy stworzenia Narodowego Zintegrowanego Systemu Monitoringu Kosmicznego Sytuacji Nadzwyczajnych i Bezpieczeństwa Ekologicznego («NIS-EKO»)',
    specialty: '183 «Technologie ochrony środowiska» (integracja z 103 «Nauki o Ziemi», 122 «Informatyka»)',
    objectSubject: 'Obiekt: Procesy monitoringu ekologicznego i geofizycznego; Przedmiot: Metody, modele matematyczne i architektura fuzji danych teledetekcji, SSA, GNSS i kontroli specjalnej',
    targetSectors: [
      'Teledetekcja satelitarna Ziemi (wielospektralna Sentinel-2, radarowa Sentinel-1 SAR)',
      'System SKAKO / SSA (monitoring przestrzeni kosmicznej, ochrona satelitów)',
      'Permanentne sieci GNSS (monitoring mikrodeformacji zapór wodnych i krasu)',
      'Specjalna kontrola geofizyczna GCKS (sejsmoakustyka, infradźwięki, zalewanie kopalń)',
      'Matematyczna fuzja danych Data Fusion (filtry Kalmana UKF, sieci bayesowskie)',
      'Baza dowodowa ekobójstwa dla międzynarodowych trybunałów i reparacji',
      'Krajowy zintegrowany system NIS-EKO i interoperacyjność z programem Copernicus UE'
    ]
  },
  en: {
    authorName: 'Banakh Andrii Stepanovych',
    scientificSupervisor: 'Doctor of Technical Sciences, Senior Researcher Volodymyr I. Prisyazhnyi',
    institutionName: 'State Scientific Institution "Institute of Ecological Restoration and Development of Ukraine"',
    regulatoryBasis: 'Order of the Ministry of Education and Science of Ukraine No. 40, Cabinet Decree No. 44 (as amended by No. 502), DSTU 8302:2015, DSTU 4163:2020',
    topic: 'Scientific, Methodological, and Technological Foundations of Creating the National Integrated System of Space Monitoring for Emergencies and Environmental Security ("NIS-ECO")',
    specialty: '183 "Environmental Protection Technologies" (interdisciplinary with 103 "Earth Sciences", 122 "Computer Science")',
    objectSubject: 'Object: Processes of spaceborne & geophysical environmental monitoring; Subject: Methods, models, and multi-source data fusion architectures for EO, SSA, GNSS, and special control',
    targetSectors: [
      'Earth Observation Remote Sensing (multispectral Sentinel-2, radar Sentinel-1 SAR, hyperspectral)',
      'Space Situational Awareness SSA/SKAKO (orbital debris cataloging & satellite protection)',
      'Permanent GNSS Networks (infrastructure deformation monitoring & tropospheric ZTD)',
      'Special Geophysical Control GCKS (seismo-acoustics, infrasound arrays, mine flooding, NPP safety)',
      'Mathematical Data Fusion (Unscented Kalman Filter UKF, dynamic Bayesian networks)',
      'Forensic ecocide evidence generation for international courts and reparations',
      'National Integrated System NIS-ECO & interoperability with EU Copernicus programme'
    ]
  }
};

export default function PhdFullDissertationGenerator({
  language,
  onLaunchSpecificAgent
}: PhdFullDissertationGeneratorProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const defaultPreset = DEMO_PRESETS[language] || DEMO_PRESETS.uk;

  const [authorName, setAuthorName] = useState(defaultPreset.authorName);
  const [scientificSupervisor, setScientificSupervisor] = useState(defaultPreset.scientificSupervisor);
  const [institutionName, setInstitutionName] = useState(defaultPreset.institutionName);
  const [regulatoryBasis, setRegulatoryBasis] = useState(defaultPreset.regulatoryBasis);
  const [topic, setTopic] = useState(defaultPreset.topic);
  const [specialty, setSpecialty] = useState(defaultPreset.specialty);
  const [objectSubject, setObjectSubject] = useState(defaultPreset.objectSubject);
  const [targetSectors, setTargetSectors] = useState<string[]>(defaultPreset.targetSectors);

  // File Upload State
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStageIdx, setCurrentStageIdx] = useState<number>(-1);
  const [chapters, setChapters] = useState<ChapterResult[]>([
    { id: 'intro', title: isPl ? 'WSTĘP' : isUk ? 'ВСТУП' : 'INTRODUCTION', stageName: isPl ? 'Wstęp' : isUk ? 'Вступ' : 'Introduction', content: '', isCompleted: false },
    { id: 'ch1', title: isPl ? 'ROZDZIAŁ 1. DOŚWIADCZENIE ŚWIATOWE I TRENDY' : isUk ? 'РОЗДІЛ 1. СВІТОВИЙ ДОСВІД ТА МІЖНАРОДНІ ТЕНДЕНЦІЇ' : 'CHAPTER 1. GLOBAL BEST PRACTICES & TRENDS', stageName: isPl ? 'Rozdział 1' : isUk ? 'Розділ 1' : 'Chapter 1', content: '', isCompleted: false },
    { id: 'ch2', title: isPl ? 'ROZDZIAŁ 2. ANALIZA STANU I ZDOLNOŚCI UKRAINY' : isUk ? 'РОЗДІЛ 2. АНАЛІЗ СТАНУ ТА СПРОМОЖНОСТЕЙ СИСТЕМИ МОНІТОРИНГУ УКРАЇНИ' : 'CHAPTER 2. UKRAINE CAPABILITIES AUDIT', stageName: isPl ? 'Rozdział 2' : isUk ? 'Розділ 2' : 'Chapter 2', content: '', isCompleted: false },
    { id: 'ch3', title: isPl ? 'ROZDZIAŁ 3. MODELE MATEMATYCZNE DATA FUSION' : isUk ? 'РОЗДІЛ 3. МЕТОДОЛОГІЧНІ ОСНОВИ ТА МАТЕМАТИЧНІ МОДЕЛІ DATA FUSION' : 'CHAPTER 3. DATA FUSION MATHEMATICAL MODELS', stageName: isPl ? 'Rozdział 3' : isUk ? 'Розділ 3' : 'Chapter 3', content: '', isCompleted: false },
    { id: 'ch4', title: isPl ? 'ROZDZIAŁ 4. ARCHITEKTURA SYSTEMU NIS-EKO' : isUk ? 'РОЗДІЛ 4. НАУКОВО-ПРАКТИЧНІ РЕКОМЕНДАЦІЇ ТА РОЗГОРТАННЯ СИСТЕМИ НІС-ЕКО' : 'CHAPTER 4. NATIONAL SYSTEM BLUEPRINT', stageName: isPl ? 'Rozdział 4' : isUk ? 'Розділ 4' : 'Chapter 4', content: '', isCompleted: false },
    { id: 'conclusions', title: isPl ? 'WNIOSKI OGÓLNE, BIBLIOGRAFIA I ZAŁĄCZNIKI' : isUk ? 'ЗАГАЛЬНІ ВИСНОВКИ, СПИСОК ДЖЕРЕЛ ТА ДОДАТКИ' : 'CONCLUSIONS, BIBLIOGRAPHY & APPENDICES', stageName: isPl ? 'Wnioski & Bibliografia' : isUk ? 'Висновки & Література' : 'Conclusions & Biblio', content: '', isCompleted: false }
  ]);

  const [activeViewTab, setActiveViewTab] = useState<'full' | 'intro' | 'ch1' | 'ch2' | 'ch3' | 'ch4' | 'conclusions'>('full');
  const [copied, setCopied] = useState(false);

  // Toggle sector selection
  const toggleSector = (sec: string) => {
    setTargetSectors(prev => 
      prev.includes(sec) ? prev.filter(s => s !== sec) : [...prev, sec]
    );
  };

  // Load 1-click Demo Preset
  const handleLoadDemo = () => {
    const p = DEMO_PRESETS[language] || DEMO_PRESETS.uk;
    setAuthorName(p.authorName);
    setScientificSupervisor(p.scientificSupervisor);
    setInstitutionName(p.institutionName);
    setRegulatoryBasis(p.regulatoryBasis);
    setTopic(p.topic);
    setSpecialty(p.specialty);
    setObjectSubject(p.objectSubject);
    setTargetSectors(p.targetSectors);
  };

  // Handle file uploads (PDF, DOCX, XLSX, CSV, TXT, XML, JSON, MD)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    setFileError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        try {
          const res = await fetch('/api/extract-text', { method: 'POST', body: formData });
          if (res.ok) {
            const data = await res.json();
            setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
          } else {
            // Text fallback for plain text, csv, json, xml, md
            if (file.type.includes('text') || file.name.endsWith('.txt') || file.name.endsWith('.csv') || file.name.endsWith('.json') || file.name.endsWith('.xml') || file.name.endsWith('.md')) {
              const text = await file.text();
              setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text }]);
            } else {
              setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл: ${file.name}, Розмір: ${(file.size / 1024).toFixed(1)} КБ]` }]);
            }
          }
        } catch (fetchErr) {
          if (file.type.includes('text') || file.name.endsWith('.txt') || file.name.endsWith('.csv') || file.name.endsWith('.json') || file.name.endsWith('.xml') || file.name.endsWith('.md')) {
            const text = await file.text();
            setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text }]);
          } else {
            console.warn('File extraction error:', fetchErr);
            setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл: ${file.name}]` }]);
          }
        }
      }
    } catch (err: any) {
      console.error('File reading process error:', err);
      setFileError(isPl ? 'Błąd przetwarzania plików' : isUk ? 'Помилка опрацювання файлів' : 'File processing error');
    } finally {
      setIsReadingFile(false);
      if (e.target) e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    setAttachedFiles([]);
  };

  // Execute full autonomous generation of all 6 stages sequentially
  const handleStartFullGeneration = async () => {
    setIsGenerating(true);
    setCurrentStageIdx(0);

    const updatedChapters = [...chapters];
    let accumulatedContext = '';

    const combinedDocText = attachedFiles.length > 0
      ? attachedFiles.map((f, i) => `=== ПЕРВИННИЙ НАУКОВИЙ МАТЕРІАЛ [${i + 1}]: ${f.name} (${(f.size / 1024).toFixed(1)} КБ) ===\n${f.text || '(Вміст зчитано)'}`).join('\n\n')
      : '';

    const fileSummaryNote = attachedFiles.length > 0
      ? ` ВРАХУВАТИ ТА ІНТЕГРУВАТИ ДАНІ, МАТЕМАТИЧНІ МОДЕЛІ Й ВИСНОВКИ З ${attachedFiles.length} ПРИКРІПЛЕНИХ ФАЙЛІВ ДОСЛІДНИКА (${attachedFiles.map(f => f.name).join(', ')}).`
      : '';

    for (let i = 0; i < STAGES.length; i++) {
      const stage = STAGES[i];
      setCurrentStageIdx(i);

      const stageName = isPl ? stage.namePl : isUk ? stage.nameUk : stage.nameEn;

      try {
        const payload = {
          agentId: stage.agentId,
          userQuery: isPl
            ? `Sporządzić akademicką część dysertacji doktora filozofii (${stage.namePl}) na temat: «${topic}». Zgłaszający: ${authorName}. Promotor: ${scientificSupervisor}. Instytucja: ${institutionName}. Specjalność: ${specialty}. Podstawa prawna: ${regulatoryBasis}. Obiekt/Przedmiot: ${objectSubject}. Kierunki: ${targetSectors.join('; ')}.${fileSummaryNote}`
            : isUk
            ? `Скласти академічну частину дисертації доктора філософії (${stage.nameUk}) на тему: «${topic}». Здобувач: ${authorName}. Науковий керівник: ${scientificSupervisor}. Наукова установа: ${institutionName}. Спеціальність: ${specialty}. Нормативно-правова база: ${regulatoryBasis}. Об'єкт/Предмет: ${objectSubject}. Охоплені напрямки: ${targetSectors.join('; ')}.${fileSummaryNote}`
            : `Synthesize the academic PhD dissertation part (${stage.nameEn}) on the topic: "${topic}". PhD Candidate: ${authorName}. Scientific Supervisor: ${scientificSupervisor}. Institution: ${institutionName}. Specialty: ${specialty}. Regulatory Framework: ${regulatoryBasis}. Object/Subject: ${objectSubject}. Key vectors: ${targetSectors.join('; ')}.${fileSummaryNote}`,
          language,
          parameters: {
            "Здобувач наукового ступеня PhD": authorName,
            "Науковий керівник": scientificSupervisor,
            "Наукова установа / ЗВО": institutionName,
            "Нормативно-правова база": regulatoryBasis,
            "Тема дисертації PhD": topic,
            "Наукова спеціальність": specialty,
            "Об'єкт та предмет дослідження": objectSubject,
            "Ключові напрямки (ДЗЗ/СКАКО/ГНСС/Спецконтроль)": targetSectors.join('; '),
            "Цільовий етап генерації": stageName,
            "Прикріплені первинні файли та наукові матеріали": combinedDocText ? combinedDocText.slice(0, 15000) : "Первинні матеріали не завантажено (використовується академічна база знань)",
            "Додаткові документи": combinedDocText ? combinedDocText.slice(0, 15000) : "",
            "supportDocs": combinedDocText ? combinedDocText.slice(0, 15000) : "",
            "Попередній контекст дослідження": accumulatedContext ? accumulatedContext.slice(-3000) : "Початок дисертаційного дослідження"
          }
        };

        const res = await fetch('/api/generate-agent-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await res.json().catch(() => ({}));
        const content = res.ok && (data.report || data.content) 
          ? (data.report || data.content) 
          : `⚠️ Помилка генерації для етапу ${stageName}: ${data.error || 'Збій API'}`;

        updatedChapters[i] = {
          ...updatedChapters[i],
          content: content,
          isCompleted: true
        };

        accumulatedContext += `\n\n=== ${stageName} ===\n` + content.slice(0, 1500);
        setChapters([...updatedChapters]);
      } catch (err: any) {
        console.error(`Error generating stage ${stage.id}:`, err);
        updatedChapters[i] = {
          ...updatedChapters[i],
          content: `⚠️ Збій генерації розділу: ${err.message || 'Мережева помилка'}`,
          isCompleted: false
        };
        setChapters([...updatedChapters]);
      }
    }

    setIsGenerating(false);
    setCurrentStageIdx(-1);

    // Auto download full dissertation once completed
    const fullText = buildFullDissertationText(updatedChapters);
    if (fullText && fullText.length > 500) {
      autoDownloadReport(fullText, `Dysertatsiia_PhD_${authorName.slice(0, 15).replace(/\s+/g, '_')}_${topic.slice(0, 20).replace(/\s+/g, '_')}`, 'phd-dissertation-suite');
    }
  };

  const buildFullDissertationText = (chList = chapters) => {
    const fileAppendix = attachedFiles.length > 0
      ? `\n**Використані первинні файли та матеріали дослідника (${attachedFiles.length}):**\n${attachedFiles.map((f, i) => `${i + 1}. ${f.name} (${(f.size / 1024).toFixed(1)} КБ)`).join('\n')}\n`
      : '';

    const header = isPl
      ? `# MINISTERSTWO EDUKACJI I NAUKI UKRAINY
## ${institutionName.toUpperCase()}
### NARODOWA AKADEMIA NAUK UKRAINY / PAŃSTWOWA AGENCJA KOSMICZNA UKRAINY
### NARODOWE CENTRUM KONTROLI I TESTOWANIA ŚRODKÓW KOSMICZNYCH

UDK 528.8:504.064:629.78  
Na prawach rękopisu  

**${authorName.toUpperCase()}**  

# ${topic.toUpperCase()}

**Dyscyplina:** ${specialty}  

### ROZPRAWA DOKTORSKA
na stopień doktora filozofii (PhD)

**Promotor naukowy:**  
${scientificSupervisor}  

**Podstawa prawna:** ${regulatoryBasis}  
**Data:** ${new Date().toLocaleDateString('pl-PL')}  
${fileAppendix}
---

### SPIS TREŚCI:
1. **WSTĘP** (Paszport naukowy, cel, 5 zadań, nowość naukowa, znaczenie)
2. **ROZDZIAŁ 1.** Doświadczenie światowe i trendy międzynarodowe
3. **ROZDZIAŁ 2.** Stan i zdolności systemu monitoringu Ukrainy
4. **ROZDZIAŁ 3.** Podstawy metodologiczne i modele matematyczne Data Fusion
5. **ROZDZIAŁ 4.** Zalecenia naukowo-praktyczne i wdrożenie systemu NIS-EKO
6. **WNIOSKI OGÓLNE**
7. **BIBLIOGRAFIA** (200+ pozycji zgodnie z normami)
8. **ZAŁĄCZNIKI A-C**

---
`
      : isEn
      ? `# MINISTRY OF EDUCATION AND SCIENCE OF UKRAINE
## ${institutionName.toUpperCase()}
### NATIONAL ACADEMY OF SCIENCES OF UKRAINE / STATE SPACE AGENCY OF UKRAINE
### NATIONAL SPACE FACILITIES CONTROL AND TEST CENTER

UDC 528.8:504.064:629.78  
As a manuscript  

**${authorName.toUpperCase()}**  

# ${topic.toUpperCase()}

**Specialty:** ${specialty}  

### DOCTORAL DISSERTATION
for the degree of Doctor of Philosophy (PhD)

**Scientific Supervisor:**  
${scientificSupervisor}  

**Regulatory Compliance:** ${regulatoryBasis}  
**Generation Date:** ${new Date().toLocaleDateString('en-US')}  
${fileAppendix}
---

### TABLE OF CONTENTS:
1. **INTRODUCTION** (Scientific passport, aim, 5 objectives, novelty, approbation)
2. **CHAPTER 1.** Global best practices and international Earth & space monitoring trends
3. **CHAPTER 2.** Ukraine's environmental and space monitoring capabilities audit
4. **CHAPTER 3.** Methodological framework and Data Fusion mathematical models
5. **CHAPTER 4.** Practical recommendations & NIS-ECO national system architecture
6. **GENERAL CONCLUSIONS**
7. **BIBLIOGRAPHY & REFERENCES** (200+ academic sources)
8. **APPENDICES A-C**

---
`
      : `# МІНІСТЕРСТВО ОСВІТИ І НАУКИ УКРАЇНИ
## ${institutionName.toUpperCase()}
### НАЦІОНАЛЬНА АКАДЕМІЯ НАУК УКРАЇНИ / ДЕРЖАВНЕ КОСМІЧНЕ АГЕНТСТВО УКРАЇНИ
### НАЦІОНАЛЬНИЙ ЦЕНТР УПРАВЛІННЯ ТА ВИПРОБУВАНЬ КОСМІЧНИХ ЗАСОБІВ

УДК 528.8:504.064:629.78  
На правах рукопису  

**${authorName.toUpperCase()}**  

# ${topic.toUpperCase()}

**Спеціальність:** ${specialty}  

### ДИСЕРТАЦІЯ
на здобуття наукового ступеня доктора філософії (PhD)

**Науковий керівник:**  
${scientificSupervisor}  

**Нормативно-правова база:** ${regulatoryBasis}  
**Дата генерації:** ${new Date().toLocaleDateString('uk-UA')}  
${fileAppendix}
---

### ЗМІСТ ДИСЕРТАЦІЙНОЇ РОБОТИ:
1. **ВСТУП** (Науковий паспорт, актуальність, мета, 5 завдань, наукова новизна, апробація)
2. **РОЗДІЛ 1.** Світовий досвід та тенденції космічно-геофізичної підтримки екологічного моніторингу
3. **РОЗДІЛ 2.** Сучасний стан, проблеми та потенціал системи космічно-екологічного контролю України
4. **РОЗДІЛ 3.** Методологічні основи та математичні моделі комплексування гетерогенних даних (Data Fusion)
5. **РОЗДІЛ 4.** Науково-практичні рекомендації та архітектура розгортання Національної системи (НІС-ЕКО)
6. **ЗАГАЛЬНІ ВИСНОВКИ**
7. **СПИСОК ВИКОРИСТАНИХ ДЖЕРЕЛ** (200+ позицій за ДСТУ 8302:2015 / APA 7th)
8. **ДОДАТКИ А-В**

---
`;

    const body = chList.map((ch) => {
      return `\n\n# ${ch.title}\n\n${ch.content || '*(Цей розділ ще очікує на генерацію)*'}\n\n---`;
    }).join('\n');

    return header + body;
  };

  const fullCompiledText = buildFullDissertationText();
  const completedCount = chapters.filter(c => c.isCompleted).length;

  const handleCopy = () => {
    const textToCopy = activeViewTab === 'full' 
      ? fullCompiledText 
      : (chapters.find(c => c.id === activeViewTab)?.content || fullCompiledText);
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadWord = () => {
    autoDownloadReport(fullCompiledText, `Dysertatsiia_PhD_${topic.slice(0, 30).replace(/\s+/g, '_')}`, 'phd-full-suite');
  };

  return (
    <div className="space-y-8 animate-fade-in font-mono">
      {/* Hero Master Suite Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-indigo-950 border-2 border-purple-500/60 rounded-3xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 bottom-0 w-72 h-72 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 space-y-4 max-w-4xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 text-[9px] font-black uppercase bg-purple-500/30 text-purple-300 border border-purple-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-purple-400 animate-pulse" />
              <span>
                {isPl 
                  ? 'AUTONOMICZNY SUPER-ORKIESTRATOR DYSERTACJI' 
                  : isUk 
                  ? 'АВТОНОМНИЙ ДИСЕРТАЦІЙНИЙ СУПЕР-ОРКЕСТРАТОР' 
                  : 'AUTONOMOUS PHD DISSERTATION SUPER-ORCHESTRATOR'}
              </span>
            </span>
            <span className="px-3 py-1 text-[9px] font-black uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>
                {isPl 
                  ? 'PEŁNA DYSPOZYCJA POD KLUCZ (WSTĘP + ROZDZIAŁY 1–4 + WNIOSKI)' 
                  : isUk 
                  ? 'ПОВНИЙ ОБСЯГ ПІД КЛЮЧ (ВСТУП + РОЗДІЛИ 1–4 + ВИСНОВКИ)' 
                  : 'FULL TURNKEY SCOPE (INTRO + CH 1–4 + CONCLUSIONS)'}
              </span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black uppercase text-white tracking-tight leading-tight">
            🚀 {isPl 
                ? 'Generator Pełnego Tekstu Dysertacji PhD w 1 Klik' 
                : isUk 
                ? 'Генератор Повного Тексту Дисертації PhD у 1 Клік' 
                : '1-Click Full PhD Dissertation Generator'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
            {isPl
              ? 'Wprowadź temat badania lub załącz pliki źródłowe — system automatycznie uruchomi sekwencyjny łańcuch 6 etapów naukowych: zsyntetyzuje Wstęp, wygeneruje Przegląd światowy (Rozdział 1), przeprowadzi audyt stanu Ukrainy (Rozdział 2), wyprowadzi modele Data Fusion (Rozdział 3), zaprojektuje System Narodowy (Rozdział 4), sformułuje wnioski oraz bibliografię i zapisze kompletny dokument Word (.docx)!'
              : isUk
              ? 'Введіть тему дослідження або прикріпіть первинні файли — система автоматично запустить наскрізний ланцюг з 6 наукових етапів: синтезує Вступ за НАЗЯВО, згенерує Світовий огляд (Розділ 1), проведе аудит стану України (Розділ 2), виведе математичні моделі Data Fusion (Розділ 3), спроєктує Національну систему (Розділ 4), сформує висновки та бібліографію за ДСТУ 8302:2015 і збере єдиний Word (.docx) документ!'
              : 'Enter your research topic or attach source files — the system autonomously executes a 6-stage doctoral pipeline: synthesizes the NAQA Introduction, generates Chapter 1 Global Trends, conducts Chapter 2 Ukraine Audit, formulates Chapter 3 Data Fusion models, blueprints Chapter 4 National System, compiles Conclusions and Bibliography, and exports a unified Word (.docx) dissertation!'}
          </p>
        </div>
      </div>

      {/* Input Parameters Box */}
      <div className="bg-slate-900 border border-purple-500/30 rounded-2xl p-6 sm:p-8 text-white shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
          <h2 className="text-sm font-extrabold uppercase text-purple-200 tracking-wider flex items-center gap-2">
            <FileText className="w-4 h-4 text-purple-400" />
            <span>
              {isPl 
                ? '1. Paszport Naukowy Dysertacji (Wprowadź Temat)' 
                : isUk 
                ? '1. Вхідний Науковий Паспорт Дисертації (Введіть Тему)' 
                : '1. Dissertation Academic Passport (Enter Topic)'}
            </span>
          </h2>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleLoadDemo}
              className="px-3 py-1 bg-gradient-to-r from-amber-500/20 to-purple-500/20 hover:from-amber-500/30 hover:to-purple-500/30 text-amber-300 hover:text-amber-200 text-[10px] font-black uppercase rounded-lg transition-all border border-amber-400/40 flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>{isPl ? '⚡ Wypełnij demo w 1 klik' : isUk ? '⚡ Заповнити демо-прикладом у 1 клік' : '⚡ 1-Click Demo Preset'}</span>
            </button>

            <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-400/30">
              ДСТУ 4163:2020 • НАЗЯВО • ПКМУ № 44
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Author, Supervisor, Institution & Regulatory Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-950/60 p-4 rounded-xl border border-purple-500/20">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
                <span>{isPl ? 'Zgłaszający / Autor (Imię i nazwisko):' : isUk ? 'Здобувач наукового ступеня PhD (ПІБ):' : 'PhD Candidate / Author Name:'}</span>
                <span className="text-[10px] text-purple-300">ПІБ автора</span>
              </label>
              <input
                type="text"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder={isPl ? 'np. Banach Andrij Stepanowycz' : isUk ? 'напр. Банах Андрій Степанович' : 'e.g. Banakh Andrii Stepanovych'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
                <span>{isPl ? 'Promotor naukowy (Tytuł, stopień, nazwisko):' : isUk ? 'Науковий керівник (ПІБ, ступінь, звання):' : 'Scientific Supervisor (Name, Degree):'}</span>
                <span className="text-[10px] text-purple-300">Керівник</span>
              </label>
              <input
                type="text"
                value={scientificSupervisor}
                onChange={(e) => setScientificSupervisor(e.target.value)}
                placeholder={isPl ? 'dr hab. inż., prof. Wołodymyr I. Przysiażnyj' : isUk ? 'доктор технічних наук, с.н.с. Присяжний Володимир Ілліч' : 'Dr. Volodymyr I. Prisyazhnyi'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
                <span>{isPl ? 'Instytucja naukowa / Uczelnia wyższa:' : isUk ? 'Наукова установа / Заклад вищої освіти:' : 'Scientific Institution / University:'}</span>
                <span className="text-[10px] text-purple-300">Установа</span>
              </label>
              <input
                type="text"
                value={institutionName}
                onChange={(e) => setInstitutionName(e.target.value)}
                placeholder={isPl ? 'Państwowa Instytucja Naukowa...' : isUk ? 'Державна наукова установа «Інститут екологічного відновлення та розвитку України»' : 'State Scientific Institution...'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
                <span>{isPl ? 'Podstawa prawna i normatywna:' : isUk ? 'Нормативно-правова база дослідження:' : 'Regulatory & Academic Compliance:'}</span>
                <span className="text-[10px] text-emerald-400 font-bold">МОН № 40 • ПКМУ № 44</span>
              </label>
              <input
                type="text"
                value={regulatoryBasis}
                onChange={(e) => setRegulatoryBasis(e.target.value)}
                placeholder={isPl ? 'Zarządzenie MEN Nr 40, RMU Nr 44...' : isUk ? 'Наказ МОН № 40, ПКМУ № 44 (зі змінами № 502), ДСТУ 8302:2015, ДСТУ 4163:2020' : 'Order of MES No. 40, CMU Resolution No. 44...'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>
                {isPl 
                  ? 'Temat rozprawy doktorskiej (PhD):' 
                  : isUk 
                  ? 'Тема дисертаційної роботи доктора філософії (PhD):' 
                  : 'PhD Dissertation Research Topic:'}
              </span>
              <span className="text-[10px] text-purple-300">
                {isPl ? 'Główny temat badania' : isUk ? 'Основна тема дослідження' : 'Core Research Title'}
              </span>
            </label>
            <textarea
              rows={2}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={isPl ? 'Wprowadź temat dysertacji...' : isUk ? 'Введіть тему дисертації...' : 'Enter dissertation topic...'}
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl p-3.5 text-xs text-slate-100 font-mono"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
                {isPl ? 'Dyscyplina naukowa (Kod i nazwa):' : isUk ? 'Наукова спеціальність (Код та назва):' : 'Scientific Specialty / Field:'}
              </label>
              <input
                type="text"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder={isPl ? 'np. 103 Nauki o Ziemi / 122 Informatyka' : isUk ? 'напр. 103 «Науки про Землю» / 122 «Комп\'ютерні науки»' : 'e.g. 103 Earth Sciences / 122 Computer Science'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
                {isPl ? 'Obiekt i przedmiot badania naukowego:' : isUk ? 'Об\'єкт та предмет наукового дослідження:' : 'Research Object & Subject:'}
              </label>
              <input
                type="text"
                value={objectSubject}
                onChange={(e) => setObjectSubject(e.target.value)}
                placeholder={isPl ? 'Obiekt: ...; Przedmiot: ...' : isUk ? 'Об\'єкт: ...; Предмет: ...' : 'Object: ...; Subject: ...'}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 rounded-xl px-4 py-2.5 text-xs text-slate-100 font-mono"
              />
            </div>
          </div>

          {/* Sector Checkboxes */}
          <div className="space-y-2 pt-2">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wide">
              {isPl 
                ? 'Priorytetowe wektory technologiczne objęte badaniem:' 
                : isUk 
                ? 'Пріоритетні технологічні вектори, що охоплюються дослідженням:' 
                : 'Key Technological & Methodological Vectors Covered:'}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                'Дистанційне зондування Землі (мультиспектральні Sentinel-2, радарні Sentinel-1 SAR, гіперспектральні)',
                'Система СКАКО (моніторинг космічного простору SSA/SST, захист супутників та ліній зв\'язку)',
                'Перманентні мережі ГНСС (моніторинг деформацій дамб ГЕС, карстів Солотвино та тропосфери ZTD)',
                'Спеціальний геофізичний контроль ГЦСК (сейсмоакустика, інфразвук, затоплення шахт Донбасу, ЗАЕС/ЧАЕС)',
                'Математичне комплексування Data Fusion (адаптивні фільтри Кальмана UKF, динамічні Байєсівські графи)',
                'Доказова база воєнного екоциду для міжнародних судових інстанцій та репарацій',
                'Національна інтегрована система НІС-ЕКО та взаємодія з програмою Copernicus ЄС'
              ].map((sec, idx) => (
                <label 
                  key={idx} 
                  onClick={() => toggleSector(sec)}
                  className={`flex items-start gap-2.5 p-2.5 rounded-xl border text-xs cursor-pointer transition-all ${
                    targetSectors.includes(sec)
                      ? 'bg-purple-950/50 border-purple-500 text-purple-100'
                      : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={targetSectors.includes(sec)}
                    onChange={() => {}}
                    className="mt-0.5 rounded border-slate-700 text-purple-600 focus:ring-0 cursor-pointer"
                  />
                  <span className="leading-snug">{sec}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* 2. File Attachments & Primary Research Data Box */}
        <div className="bg-slate-950/80 border border-purple-500/40 rounded-2xl p-5 sm:p-6 text-white shadow-lg space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-xl bg-purple-600/20 text-purple-400 border border-purple-500/30">
                <Upload className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-xs font-black uppercase text-purple-200 tracking-wide flex items-center gap-2">
                  <span>
                    {isPl
                      ? '2. Materiały Źródłowe, Publikacje i Dane Badawcze (PDF, DOCX, XLSX, TXT)'
                      : isUk
                      ? '2. Первинні Наукові Матеріали, Статті та Файли Дослідження (PDF, DOCX, XLSX, TXT)'
                      : '2. Primary Research Materials, Papers & Datasets (PDF, DOCX, XLSX, TXT)'}
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400 font-sans mt-0.5">
                  {isPl
                    ? 'Załącz artykuły naukowe, raporty geofizyczne/satelitarne, tabele pomiarowe lub akty prawne. AI zintegruje je do wszystkich 6 rozdziałów dysertacji.'
                    : isUk
                    ? 'Завантажте ваші статті, супутникові/геофізичні звіти, таблиці експериментів чи нормативні акти. ШІ опрацює їх та інтегрує у всі 6 розділів дисертації.'
                    : 'Attach your scientific papers, satellite/geophysical reports, experimental tables, or regulations. AI will integrate them across all 6 dissertation chapters.'}
                </p>
              </div>
            </div>

            {attachedFiles.length > 0 && (
              <button
                onClick={clearAllFiles}
                className="text-[11px] font-bold text-red-400 hover:text-red-300 px-3 py-1 bg-red-950/40 hover:bg-red-900/50 border border-red-500/30 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isPl ? 'Wyczyść wszystkie' : isUk ? 'Очистити всі' : 'Clear all'}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-1">
            <label className="px-4 py-2.5 bg-gradient-to-r from-purple-900/80 to-indigo-900/80 hover:from-purple-800 hover:to-indigo-800 border border-purple-400/50 text-purple-100 text-xs font-extrabold uppercase rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-md hover:scale-[1.01]">
              <Upload className="w-4 h-4 text-purple-300" />
              <span>
                {isReadingFile
                  ? (isPl ? 'Odczytywanie plików...' : isUk ? 'Зчитування та вилучення тексту...' : 'Reading files...')
                  : (isPl ? '➕ Załącz pliki badawcze' : isUk ? '➕ Прикріпити файли дослідження' : '➕ Attach Research Files')}
              </span>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.json,.txt,.xml,.md"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isReadingFile || isGenerating}
              />
            </label>

            <div className="text-[11px] text-slate-400 font-sans flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>
                {isPl
                  ? `Załączono: ${attachedFiles.length} plik(ów)`
                  : isUk
                  ? `Прикріплено: ${attachedFiles.length} файл(ів)`
                  : `Attached: ${attachedFiles.length} file(s)`}
              </span>
            </div>
          </div>

          {/* Attached Files Badges */}
          {attachedFiles.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-2">
              {attachedFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-2 p-2.5 bg-slate-900 border border-purple-500/40 rounded-xl text-xs text-purple-100 shadow-xs group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileCheck className="w-4 h-4 text-purple-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-bold truncate text-[11px] text-purple-200" title={file.name}>
                        {file.name}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {(file.size / 1024).toFixed(1)} KB • {file.text ? `${file.text.length} симв.` : 'Готово'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(idx)}
                    className="text-slate-400 hover:text-red-400 p-1 rounded-md hover:bg-slate-800 transition-colors cursor-pointer shrink-0"
                    title={isUk ? "Видалити файл" : "Remove file"}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {fileError && (
            <div className="p-3 bg-red-950/50 border border-red-500/40 rounded-xl text-xs text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}
        </div>

        {/* Master Launch Button */}
        <div className="pt-4 border-t border-slate-800 flex items-center justify-between flex-wrap gap-4">
          <div className="text-xs text-slate-400">
            <span className="font-bold text-purple-300">
              {isPl ? 'Status syntezy:' : isUk ? 'Статус синтезу:' : 'Synthesis Status:'}
            </span> {completedCount} {isPl ? 'z 6 rozdziałów' : isUk ? 'з 6 розділів згенеровано' : 'of 6 chapters generated'}
            {attachedFiles.length > 0 && (
              <span className="ml-2 text-emerald-400 font-bold">
                (+{attachedFiles.length} {isPl ? 'plików' : isUk ? 'файлів' : 'files'})
              </span>
            )}
          </div>

          <button
            onClick={handleStartFullGeneration}
            disabled={isGenerating || !topic.trim()}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-black text-xs uppercase rounded-xl transition-all shadow-xl shadow-purple-600/40 flex items-center gap-3 cursor-pointer hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-purple-200" />
                <span>
                  {isPl 
                    ? `Generowanie etapu ${currentStageIdx + 1} z 6...` 
                    : isUk 
                    ? `Генерація етапу ${currentStageIdx + 1} з 6...` 
                    : `Generating stage ${currentStageIdx + 1} of 6...`}
                </span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-purple-200" />
                <span>
                  🚀 {isPl 
                    ? 'Wygeneruj Pełną Dysertację pod Klucz w Word (.docx)' 
                    : isUk 
                    ? 'Згенерувати Повну Дисертацію під Ключ у Word (.docx)' 
                    : 'Generate Full Turnkey PhD Dissertation (.docx)'}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 6-Stage Progress Pipeline */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white shadow-xl space-y-4">
        <h3 className="text-xs font-bold uppercase text-slate-300 tracking-wider flex items-center gap-2">
          <Layers className="w-4 h-4 text-purple-400" />
          <span>
            {isPl 
              ? 'Krokowy Rurociąg Akademickiego Badania Doktorskiego:' 
              : isUk 
              ? 'Покроковий Академічний Пайплайн Дисертаційного Дослідження:' 
              : 'Sequential Academic PhD Dissertation Pipeline:'}
          </span>
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {STAGES.map((stg, idx) => {
            const isCurrent = currentStageIdx === idx;
            const isDone = chapters[idx]?.isCompleted;
            const stgName = isPl ? stg.namePl : isUk ? stg.nameUk : stg.nameEn;
            const stgDesc = isPl ? stg.descPl : isUk ? stg.descUk : stg.descEn;

            return (
              <div 
                key={stg.id}
                className={`p-4 rounded-xl border transition-all ${
                  isCurrent 
                    ? 'bg-purple-950/70 border-purple-400 shadow-md shadow-purple-500/20 animate-pulse'
                    : isDone
                    ? 'bg-slate-950 border-emerald-500/50 text-slate-200'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-slate-900 border border-slate-800">
                    {isPl ? `Etap ${idx + 1}` : isUk ? `Етап ${idx + 1}` : `Stage ${idx + 1}`}
                  </span>
                  {isDone ? (
                    <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {isPl ? 'Gotowe' : isUk ? 'Готово' : 'Done'}
                    </span>
                  ) : isCurrent ? (
                    <span className="text-[10px] font-bold text-purple-300 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 animate-spin" /> {isPl ? 'Generowanie...' : isUk ? 'Генерація...' : 'Generating...'}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-500">{isPl ? 'Oczekuje' : isUk ? 'Очікує' : 'Pending'}</span>
                  )}
                </div>
                <div className="text-xs font-bold text-slate-100">{stgName}</div>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 leading-relaxed font-sans">{stgDesc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Generated Dissertation Canvas & Chapter Explorer */}
      {completedCount > 0 && (
        <div className="bg-slate-900 border border-purple-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-3">
            <div>
              <h3 className="text-base font-extrabold uppercase text-emerald-300 tracking-wider flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span>
                  {isPl 
                    ? `Zbiór Dysertacyjny Wygenerowany (${completedCount} / 6 rozdziałów)` 
                    : isUk 
                    ? `Дисертаційний Комплекс Згенеровано (${completedCount} / 6 розділів)` 
                    : `PhD Dissertation Package Generated (${completedCount} / 6 chapters)`}
                </span>
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isPl 
                  ? 'Format: Standard NAQA / DSTU 4163:2020 • Gotowy do obrony i recenzji' 
                  : isUk 
                  ? 'Формат: ДСТУ 4163:2020 / НАЗЯВО • Готовий до захисту та рецензування' 
                  : 'Format: NAQA / DSTU 4163:2020 • Ready for committee review and defense'}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? (isPl ? 'Skopiowano!' : isUk ? 'Скопійовано!' : 'Copied!') : (isPl ? 'Kopiuj' : isUk ? 'Копіювати' : 'Copy')}</span>
              </button>

              <button
                onClick={handleDownloadWord}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-black uppercase rounded-lg transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{isPl ? 'Pobierz Pełną Dysertację (.docx)' : isUk ? 'Завантажити Повну Дисертацію (.docx)' : 'Download Full Dissertation (.docx)'}</span>
              </button>

              <TelegramExportButton
                title={isPl ? "PhD Dysertacja (Kompleks)" : isUk ? "PhD Дисертація (Повний Комплекс)" : "PhD Dissertation (Full Suite)"}
                content={fullCompiledText}
                agentName={isPl ? "PhD Dysertacja" : isUk ? "PhD Дисертація" : "PhD Dissertation"}
                agentId="phd-dissertation-lead-orchestrator"
                isUk={isUk}
              />
            </div>
          </div>

          {/* Chapter Navigation Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-800">
            <button
              onClick={() => setActiveViewTab('full')}
              className={`px-3.5 py-1.5 text-xs font-extrabold uppercase rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                activeViewTab === 'full'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              📄 {isPl ? 'Pełny Tekst Zbiorczy' : isUk ? 'Повний Зведений Текст' : 'Full Compiled Text'}
            </button>

            {chapters.map((ch) => (
              <button
                key={ch.id}
                onClick={() => setActiveViewTab(ch.id as any)}
                className={`px-3.5 py-1.5 text-xs font-extrabold uppercase rounded-lg transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeViewTab === ch.id
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                    : ch.isCompleted
                    ? 'bg-slate-950 text-emerald-300 hover:bg-slate-800 border border-slate-800'
                    : 'bg-slate-950/50 text-slate-500 border border-slate-800/50'
                }`}
              >
                <span>{ch.stageName}</span>
                {ch.isCompleted && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
              </button>
            ))}
          </div>

          {/* Text View Canvas */}
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 font-sans text-xs text-slate-200 leading-relaxed overflow-x-auto max-h-[750px] overflow-y-auto whitespace-pre-wrap selection:bg-purple-500 selection:text-white">
            {activeViewTab === 'full'
              ? fullCompiledText
              : (chapters.find(c => c.id === activeViewTab)?.content || (isPl ? 'Rozdział w trakcie generowania...' : isUk ? 'Розділ ще формується...' : 'Chapter is being generated...'))}
          </div>
        </div>
      )}
    </div>
  );
}
